import { useEffect } from "react";
import { useParams, useLocation, Navigate, Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  MapPin,
  Clock,
  IndianRupee,
  Car,
  Users,
  Shield,
  ArrowRightLeft,
} from "lucide-react";
import { ROUTES, COMMON_BENEFITS, HOW_IT_WORKS } from "./routesData";

const SITE_URL = "https://ridemateapp.in";

/**
 * Update <head> meta tags for SEO on route change.
 * Direct DOM manipulation keeps it dependency-free and works for a CRA SPA.
 */
function useRouteSEO(route) {
  useEffect(() => {
    if (!route) return;
    const canonical = `${SITE_URL}/${route.slug}`;
    const title = `${route.tagline} | RideMate`;
    const description = `${route.from} to ${route.to} shared rides & carpooling on RideMate. Distance ${route.distanceKm} km. Split fuel and travel cost with verified Trip Hosts — book seats in the app.`;

    document.title = title;

    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:image", `${SITE_URL}/og-image.png`);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", `${SITE_URL}/og-image.png`);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;

    // JSON-LD structured data
    const oldLd = document.getElementById("route-jsonld");
    if (oldLd) oldLd.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "route-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      name: `${route.from} to ${route.to} Carpool & Ride Sharing`,
      description,
      touristType: "Commuters, students, professionals",
      itinerary: {
        "@type": "ItemList",
        itemListElement: [
          {
            "@type": "Place",
            name: route.from,
            address: {
              "@type": "PostalAddress",
              addressLocality: route.from,
              addressRegion: route.fromState,
              addressCountry: "IN",
            },
          },
          {
            "@type": "Place",
            name: route.to,
            address: {
              "@type": "PostalAddress",
              addressLocality: route.to,
              addressRegion: route.toState,
              addressCountry: "IN",
            },
          },
        ],
      },
      provider: {
        "@type": "Organization",
        name: "RideMate",
        url: SITE_URL,
        logo: `${SITE_URL}/icon-512.png`,
      },
    });
    document.head.appendChild(ld);

    // FAQ schema
    const oldFaq = document.getElementById("route-faq-jsonld");
    if (oldFaq) oldFaq.remove();
    if (route.faqs?.length) {
      const faqLd = document.createElement("script");
      faqLd.type = "application/ld+json";
      faqLd.id = "route-faq-jsonld";
      faqLd.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: route.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
      document.head.appendChild(faqLd);
    }

    // Breadcrumb schema — shows path in Google search results
    const oldBc = document.getElementById("route-breadcrumb-jsonld");
    if (oldBc) oldBc.remove();
    const bcLd = document.createElement("script");
    bcLd.type = "application/ld+json";
    bcLd.id = "route-breadcrumb-jsonld";
    bcLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Popular Routes",
          item: `${SITE_URL}/#popular-routes`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: `${route.from} to ${route.to}`,
          item: canonical,
        },
      ],
    });
    document.head.appendChild(bcLd);

    window.scrollTo(0, 0);

    return () => {
      // Reset on unmount so other pages get default title
      document.title = "RideMate — Post a ride or find one going your way | Coming Soon";
    };
  }, [route]);
}

export default function RoutePage() {
  const params = useParams();
  const location = useLocation();
  const slug = params.routeSlug || location.pathname.replace(/^\/+/, "").split("/")[0];
  const route = ROUTES[slug];
  useRouteSEO(route);

  if (!route) return <Navigate to="/" replace />;

  const reverseSlug = `${route.to.toLowerCase()}-to-${route.from.toLowerCase()}`;
  const reverseExists = !!ROUTES[reverseSlug];
  const otherRoutes = Object.values(ROUTES).filter((r) => r.slug !== route.slug);

  return (
    <main
      data-testid={`route-page-${route.slug}`}
      className="pt-12 pb-20 bg-white"
    >
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#F9FAFB] border-b border-[#e5e7eb]">
        <div
          className="absolute inset-0 -z-10 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at center, rgba(36,48,63,0.14) 1px, transparent 1.5px)",
            backgroundSize: "28px 28px",
            maskImage:
              "radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)",
          }}
        />
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 md:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-body text-sm text-gray-600 hover:text-[#5FA836] transition-colors"
            data-testid="route-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="mt-6 flex items-start gap-5">
            <div className="h-12 w-12 rounded-2xl bg-[#5FA836]/10 grid place-items-center shrink-0 overflow-hidden">
              <img
                src="/nav-icon.png"
                alt="RideMate"
                width="34"
                height="48"
                className="h-9 w-auto object-contain"
                draggable={false}
              />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
                Route · {route.from} → {route.to}
              </span>
              <h1 className="mt-2 font-display text-4xl md:text-6xl font-black tracking-[-0.03em] leading-[1] text-[#24303F]">
                {route.tagline}
              </h1>
              <p className="mt-6 font-body text-gray-700 text-base md:text-lg leading-relaxed max-w-3xl">
                {route.heroBlurb}
              </p>
            </div>
          </div>

          {/* Route stats */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl">
            <Stat icon={MapPin} label="Distance" value={`${route.distanceKm} km`} />
            <Stat icon={Clock} label="Travel time" value={`${route.durationHours} hrs`} />
            <Stat icon={IndianRupee} label="Fare model" value="Split cost" />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/#waitlist"
              data-testid="route-cta-waitlist"
              className="btn-primary rounded-full px-7 py-3 font-display inline-flex items-center gap-2"
            >
              Join Waitlist <ArrowRight className="w-4 h-4" />
            </Link>
            {reverseExists && (
              <Link
                to={`/${reverseSlug}`}
                data-testid="route-cta-reverse"
                className="btn-ghost rounded-full px-6 py-3 font-display inline-flex items-center gap-2"
              >
                <ArrowRightLeft className="w-4 h-4" />
                {route.to} → {route.from}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Intro + highlights */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
              About this route
            </span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-[-0.03em] text-[#24303F]">
              The smarter way to travel from {route.from} to {route.to}
            </h2>
            <p className="mt-5 font-body text-gray-700 text-[16px] leading-[1.85]">
              {route.intro}
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {route.highlights.map((h, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-2xl border border-[#e5e7eb] bg-[#F9FAFB] p-4"
                >
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-[#5FA836] shrink-0" />
                  <span className="font-body text-[14.5px] text-[#24303F]">{h}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison side card */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-[#5FA836]/30 bg-gradient-to-br from-[#5FA836]/8 via-white to-white p-7">
              <div className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
                How you pay
              </div>
              <div className="mt-5 space-y-4">
                <CompareRow label="Solo private cab" value="Full cost" muted />
                <CompareRow label="RideMate shared ride" value="Split cost" highlight />
                <div className="h-px bg-[#e5e7eb]" />
                <div className="font-body text-sm text-gray-600">
                  You save up to{" "}
                  <span className="font-display font-bold text-[#5FA836]">
                    60%
                  </span>{" "}
                  per trip by pooling — plus a friendlier ride.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-[#F9FAFB] border-y border-[#e5e7eb]">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
            Why RideMate for {route.from} → {route.to}
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-[-0.03em] text-[#24303F]">
            Six reasons ride-sharing wins.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {COMMON_BENEFITS.map((b, i) => (
              <div
                key={i}
                className="rounded-2xl border border-[#e5e7eb] bg-white p-6 lift"
              >
                <div className="h-9 w-9 rounded-lg bg-[#5FA836]/10 grid place-items-center text-[#5FA836] mb-4">
                  {i === 0 && <IndianRupee className="w-4 h-4" />}
                  {i === 1 && <Shield className="w-4 h-4" />}
                  {i === 2 && <Clock className="w-4 h-4" />}
                  {i === 3 && <MapPin className="w-4 h-4" />}
                  {i === 4 && <Car className="w-4 h-4" />}
                  {i === 5 && <Users className="w-4 h-4" />}
                </div>
                <h3 className="font-display font-bold text-lg text-[#24303F]">
                  {b.title}
                </h3>
                <p className="mt-2 font-body text-[14.5px] text-gray-600 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
            How it works
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-[-0.03em] text-[#24303F]">
            Book a {route.from} to {route.to} ride in three steps.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {HOW_IT_WORKS.map((s) => (
              <div
                key={s.n}
                className="rounded-2xl border border-[#e5e7eb] bg-[#F9FAFB] p-7"
              >
                <div className="font-display text-5xl font-black text-[#5FA836]">
                  {s.n}
                </div>
                <h3 className="mt-4 font-display font-bold text-lg text-[#24303F]">
                  {s.t}
                </h3>
                <p className="mt-2 font-body text-[14.5px] text-gray-600 leading-relaxed">
                  {s.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-[#F9FAFB] border-y border-[#e5e7eb]">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
            FAQ
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-[-0.03em] text-[#24303F]">
            Frequently asked — {route.from} to {route.to}
          </h2>
          <div className="mt-10 space-y-4">
            {route.faqs.map((f, i) => (
              <details
                key={i}
                data-testid={`route-faq-${i}`}
                className="group rounded-2xl border border-[#e5e7eb] bg-white p-5"
              >
                <summary className="cursor-pointer font-display text-base md:text-lg font-semibold text-[#24303F] flex items-center justify-between">
                  {f.q}
                  <span className="ml-4 text-[#5FA836] transition-transform group-open:rotate-45 text-2xl leading-none font-light">
                    +
                  </span>
                </summary>
                <p className="mt-3 font-body text-[15px] text-gray-700 leading-[1.85]">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Download / CTA */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-black tracking-[-0.03em] text-[#24303F]">
            Ready to ride together?
          </h2>
          <p className="mt-5 font-body text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
            RideMate launches soon on Android and iOS. Join the waitlist and be
            first to book a {route.from} to {route.to} ride the day we go live.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/#waitlist"
              data-testid="route-bottom-cta-waitlist"
              className="btn-primary rounded-full px-7 py-3 font-display inline-flex items-center gap-2"
            >
              Join Waitlist <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="rounded-full border border-[#e5e7eb] bg-white px-5 py-3">
              <span className="font-body text-xs uppercase tracking-[0.2em] text-gray-500">
                Coming soon
              </span>{" "}
              <span className="font-display font-bold text-[#24303F]">
                Play Store · App Store
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Other routes */}
      <section className="py-16 md:py-20 bg-[#F9FAFB] border-t border-[#e5e7eb]">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
            Also popular
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-[-0.03em] text-[#24303F]">
            Explore other routes on RideMate
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherRoutes.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`route-related-${r.slug}`}
                className="group flex items-center gap-4 rounded-2xl border border-[#e5e7eb] bg-white p-5 hover:border-[#5FA836]/60 transition-colors"
              >
                <div className="h-10 w-10 rounded-xl bg-[#5FA836]/10 grid place-items-center text-[#5FA836] shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-bold text-[#24303F]">
                    {r.from} → {r.to}
                  </div>
                  <div className="font-body text-xs text-gray-500 mt-0.5">
                    {r.distanceKm} km · Split cost
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#e5e7eb] bg-white p-5 flex items-center gap-4">
    <div className="h-10 w-10 rounded-xl bg-[#5FA836]/10 grid place-items-center text-[#5FA836]">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="font-body text-xs text-gray-500 uppercase tracking-[0.15em]">
        {label}
      </div>
      <div className="font-display text-xl font-black text-[#24303F]">
        {value}
      </div>
    </div>
  </div>
);

const CompareRow = ({ label, value, muted, highlight }) => (
  <div
    className={`flex items-center justify-between rounded-xl px-4 py-3 border ${
      highlight
        ? "border-[#5FA836]/40 bg-[#5FA836]/5"
        : "border-[#e5e7eb] bg-white"
    }`}
  >
    <span
      className={`font-body text-sm ${muted ? "text-gray-500" : "text-[#24303F] font-semibold"}`}
    >
      {label}
    </span>
    <span
      className={`font-display font-black ${
        highlight ? "text-[#5FA836]" : muted ? "text-gray-500 line-through" : "text-[#24303F]"
      }`}
    >
      {value}
    </span>
  </div>
);
